import { ICinematics } from '@/types'
import { isEmpty } from '@/utils/nunchee-kit'

export type MergeStrategy = 'replace' | 'merge' | 'deep-merge' | 'append'

export interface MergeEntry {
  path: string
  strategy: MergeStrategy
  properties?: string[]
}

export interface MergeCinematicsOptions {
  base: ICinematics
  override: ICinematics
  config?: MergeEntry[]
}

export const DEFAULT_MERGE_CONFIG: MergeEntry[] = [
  { path: 'ambiance.palette', strategy: 'replace' },
  { path: 'ambiance.images', strategy: 'replace' },
  { path: 'props.banners', strategy: 'replace' },
  { path: 'props.modals', strategy: 'replace' },
  { path: 'props.search', strategy: 'merge' },
  { path: 'props.navbar.items', strategy: 'replace' },
  { path: 'props.navbar', strategy: 'merge', properties: ['hasLogin', 'hasProfile', 'hasLogo', 'hasProfileLogo', 'hasSearch'] },
  { path: 'props.footer', strategy: 'replace' },
  // Lets branding or an A/B variant switch a segment to the custom player controls.
  { path: 'props.player', strategy: 'replace' },
  { path: 'scenes', strategy: 'merge' },
]

const applyMergeStrategy = (
  target: any,
  source: any,
  strategy: MergeStrategy
): any => {
  if (isEmpty(source)) return target

  switch (strategy) {
  case 'replace':
    return source

  case 'merge':
    if (typeof target === 'object' && typeof source === 'object') {
      return { ...target, ...source }
    }
    return source

  case 'deep-merge':
    if (typeof target === 'object' && typeof source === 'object') {
      const result = { ...target }
      Object.keys(source).forEach(key => {
        if (typeof source[key] === 'object' && !Array.isArray(source[key])) {
          result[key] = applyMergeStrategy(result[key] || {}, source[key], 'deep-merge')
        } else {
          result[key] = source[key]
        }
      })
      return result
    }
    return source

  case 'append':
    if (Array.isArray(target) && Array.isArray(source)) {
      return [...target, ...source]
    }
    return source

  default:
    return source
  }
}

const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((current, key) => current?.[key], obj)
}

const setNestedValue = (obj: any, path: string, value: any): void => {
  const keys = path.split('.')
  const lastKey = keys.pop()!
  const target = keys.reduce((current, key) => {
    if (!current || !current[key]) {
      if (!current) return {}
      current[key] = {}
    }
    return current[key]
  }, obj)
  target[lastKey] = value
}

export function mergeCinematics({
  base,
  override,
  config = DEFAULT_MERGE_CONFIG
}: MergeCinematicsOptions): ICinematics {
  const newCinematics = JSON.parse(JSON.stringify(base || {})) as ICinematics

  Object.entries(config).forEach(([, entry]) => {
    const sourceValue = getNestedValue(override, entry.path)

    if (!isEmpty(sourceValue)) {
      const targetValue = getNestedValue(newCinematics, entry.path)

      if (entry.properties && typeof sourceValue === 'object') {
        const merged = { ...targetValue }
        entry.properties.forEach(prop => {
          if (sourceValue[prop] !== undefined) {
            merged[prop] = sourceValue[prop]
          }
        })
        setNestedValue(newCinematics, entry.path, merged)
      } else {
        const mergedValue = applyMergeStrategy(targetValue, sourceValue, entry.strategy)
        setNestedValue(newCinematics, entry.path, mergedValue)
      }
    }
  })

  return newCinematics
}
